import { Link, useParams } from 'react-router-dom';
import { DropCard } from '../components/DropCard';
import { getCreator, dropsByCreator } from '../data/mock';
import { formatCount } from '../lib/format';

/**
 * Creator Profile — the creator's "column." Cover + avatar + bio, a stats line,
 * the channels they publish in, and a grid of their Originals. Their latest
 * active drop is pinned above the grid. See BRAND.md ▸ Creator Profile.
 *
 * The :handle route param arrives with a leading "@" (URLs look like /@handle).
 * We strip it; a param without "@" is treated as not-found.
 */
export function CreatorProfile() {
  const { handle: raw } = useParams();
  const handle = raw?.startsWith('@') ? raw.slice(1) : undefined;
  const creator = handle ? getCreator(handle) : undefined;

  if (!creator) {
    return (
      <main className="mx-auto max-w-7xl px-6 py-24 text-center">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Creator not found</h1>
        <p className="mt-3 text-provny-muted">This creator may have moved or never existed.</p>
        <Link to="/explore" className="mt-6 inline-block font-semibold text-provny-cyber-dim hover:underline dark:text-provny-cyber">
          ← Back to Explore
        </Link>
      </main>
    );
  }

  const drops = dropsByCreator(creator.handle);
  const active = drops.filter((d) => d.edition.sold < d.edition.total);
  const pinned = active[active.length - 1]; // last active drop in source order
  const dropsCount = active.length; // drops still open to collect

  // Distinct channels this creator publishes in, derived from their drops.
  const channels = Array.from(
    new Map(drops.map((d) => [d.channelSlug, { slug: d.channelSlug, name: d.channel }])).values(),
  );

  return (
    <main className="min-h-[100dvh] bg-provny-white text-provny-black dark:bg-provny-ink dark:text-provny-white">
      {/* Cover */}
      <div className="relative aspect-[3/1] w-full overflow-hidden sm:aspect-[4/1]">
        <img src={creator.cover} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
      </div>

      <div className="mx-auto max-w-7xl px-6 pb-20">
        {/* Identity — avatar overlaps the cover */}
        <div className="-mt-12 flex flex-col gap-4 sm:-mt-14 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex items-end gap-4">
            <img
              src={creator.avatar}
              alt={`@${creator.handle}`}
              className="h-24 w-24 rounded-full border-4 border-provny-white object-cover dark:border-provny-ink sm:h-28 sm:w-28"
            />
            <div className="pb-1">
              <h1 className="font-heading text-3xl font-black tracking-tight sm:text-4xl">@{creator.handle}</h1>
              <p className="mt-1 max-w-xl text-provny-muted">{creator.bio}</p>
            </div>
          </div>
          <button className="shrink-0 rounded-xl bg-provny-cyber px-5 py-2.5 text-sm font-semibold text-provny-ink transition hover:bg-provny-cyber/90">
            Follow
          </button>
        </div>

        {/* Stats + channels */}
        <p className="mt-5 font-mono text-sm text-provny-muted">
          {formatCount(drops.length)} originals · {formatCount(dropsCount)} drops · {formatCount(creator.collected)} collected
        </p>
        {channels.length > 0 && (
          <p className="mt-2 text-sm text-provny-muted">
            Publishes in:{' '}
            {channels.map((c, i) => (
              <span key={c.slug}>
                {i > 0 && ', '}
                <Link to={`/c/${c.slug}`} className="font-medium text-provny-cyber-dim hover:underline dark:text-provny-cyber">
                  {c.name}
                </Link>
              </span>
            ))}
          </p>
        )}

        {/* Pinned latest drop */}
        {pinned && (
          <section className="mt-12">
            <h2 className="mb-4 font-heading text-xl font-bold tracking-tight">Pinned</h2>
            <div className="max-w-xs">
              <DropCard drop={pinned} />
            </div>
          </section>
        )}

        {/* Originals grid */}
        <section className="mt-12">
          <h2 className="mb-4 font-heading text-xl font-bold tracking-tight">Originals</h2>
          {drops.length > 0 ? (
            <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
              {drops.map((drop) => (
                <DropCard key={drop.id} drop={drop} />
              ))}
            </div>
          ) : (
            <p className="text-provny-muted">No originals published yet.</p>
          )}
        </section>
      </div>
    </main>
  );
}
